{
  "README": [
    "Product terminology — the single source of truth for how a Tonnie term is",
    "rendered in each locale. Consumed by THREE things that used to disagree:",
    "the runtime translator (apps/brain-service), the build-time locale pipeline",
    "(packages/locale/scripts) and the banned-term scan (scripts/qa).",
    "",
    "It lives in @tonnie/foundation and not in @tonnie/locale because",
    "@tonnie/locale is bundler-only and brain-service cannot import it (DW6-1).",
    "It is JSON and not TypeScript because the build-time pipeline is .mjs and",
    "cannot import a .ts.",
    "",
    "── entry shape ───────────────────────────────────────────────────────────",
    "  id       stable identifier; appears in cache keys and scan output",
    "  kind     'brand' = never translated, masked before the model sees it",
    "           'term'  = translated, but only to the approved rendering",
    "  rev      REVISION COUNT, and it is load-bearing for the cache. See below.",
    "  match    English source pattern, matched whole-word, case-insensitive",
    "  scope    'text' (default) matches source text; 'keys' matches only the",
    "           listed locale key paths and takes no part in translation",
    "  translationOnly  true = a rule about how a TRANSLATION is written (register,",
    "           regional vocabulary), so the scan checks locale values only and never",
    "           an English source config — where a QultureUp lesson may legitimately",
    "           quote the very word ('Using “você” can sound overly direct')",
    "  locales  per-locale { approved, banned } for a 'term'",
    "",
    "── rev, and why it is not a version number ───────────────────────────────",
    "  rev 0 means 'exactly as the caches were populated'. A rev-0 entry adds",
    "  NOTHING to a cache key, so every key written before this file existed",
    "  stays byte-identical and keeps hitting.",
    "",
    "  Bump rev when an approved rendering CHANGES. Only strings containing that",
    "  term then miss. The alternative — a global cache-prefix bump — re-",
    "  translates all 2055 cached strings x 4 locales (~8220 model calls),",
    "  including the entire 1809-string QultureUp catalogue, to fix a handful of",
    "  words. Scoped invalidation is ~124 calls. Any suffix segment that is",
    "  ALWAYS present is a full flush wearing a different name; do not add one.",
    "",
    "── adding a term ─────────────────────────────────────────────────────────",
    "  1. add the entry with rev 1 (new terms invalidate the strings they touch)",
    "  2. run `npm run qa:terminology` — it fails, listing every locale value",
    "     that renders the term some other way",
    "  3. fix those locale values; re-run until green",
    "  4. `npm run i18n:check` must stay green (parity + brand drift)"
  ],
  "terms": [
    {
      "id": "brand-tonnie-checked",
      "kind": "brand",
      "rev": 0,
      "match": "Tonnie Checked"
    },
    { "id": "brand-tonnie-citizen", "kind": "brand", "rev": 0, "match": "Tonnie Citizen" },
    { "id": "brand-tonnie-business", "kind": "brand", "rev": 0, "match": "Tonnie Business" },
    { "id": "brand-tonnie-brain", "kind": "brand", "rev": 0, "match": "Tonnie Brain" },
    { "id": "brand-tonnie", "kind": "brand", "rev": 0, "match": "Tonnie" },
    { "id": "brand-citizen", "kind": "brand", "rev": 0, "match": "Citizen" },
    { "id": "brand-checkup", "kind": "brand", "rev": 0, "match": "CheckUp" },
    { "id": "brand-teleportal", "kind": "brand", "rev": 0, "match": "TelePortal" },
    { "id": "brand-qultureup", "kind": "brand", "rev": 0, "match": "QultureUp" },
    { "id": "brand-qultureup-caps", "kind": "brand", "rev": 0, "match": "QultureUP" },
    { "id": "brand-copilot", "kind": "brand", "rev": 0, "match": "CoPilot" },
    { "id": "brand-copilot-lower", "kind": "brand", "rev": 0, "match": "Copilot" },
    { "id": "brand-iqulture-caps", "kind": "brand", "rev": 0, "match": "IQulture" },
    { "id": "brand-iqulture", "kind": "brand", "rev": 0, "match": "iQulture" },
    { "id": "brand-mesh", "kind": "brand", "rev": 0, "match": "MESH" },
    { "id": "brand-passport", "kind": "brand", "rev": 0, "match": "Passport" },
    { "id": "brand-career", "kind": "brand", "rev": 0, "match": "Career" },
    { "id": "brand-care", "kind": "brand", "rev": 0, "match": "Care" },
    {
      "id": "brand-miles",
      "kind": "brand",
      "rev": 1,
      "match": "Miles",
      "note": "Miles is QultureUp's branded currency, like a point balance — en.json already treats it as a name ('{{count}} Miles'), but the four locales translated it (Milhas / Millas / Miglia) in 281 strings. rev 1 because adding it to the brand list changes how every string containing it translates."
    },
    {
      "id": "household",
      "kind": "term",
      "rev": 1,
      "match": "household",
      "note": "The machine translator rendered 'Household' as 'Doméstico' — a domestic WORKER, not the people you live with. It reached production on the TelePortal setup guide as 'Seu Doméstico'. The banned list is the set of renderings that are WRONG, not everything that differs from `approved`: a household is the PEOPLE (and pets) you live with, so 'casa' / 'lar' / 'residência' — the dwelling — are wrong too, while 'família' in running prose is a legitimate paraphrase. Requiring one rendering inside free prose would mean re-translating 61 English strings with correct agreement in four languages; mechanising that produces worse copy, not more consistent copy. Labels, where the word IS the term, are pinned exactly by `household-label` below.",
      "locales": {
        "pt-PT": {
          "approved": "agregado familiar",
          "banned": ["doméstico", "doméstica", "domésticos", "domésticas", "casa", "lar"]
        },
        "pt-BR": {
          "approved": "núcleo familiar",
          "banned": ["doméstico", "doméstica", "domésticos", "domésticas", "casa", "lar"]
        },
        "es-ES": {
          "approved": "hogar",
          "banned": ["doméstico", "doméstica", "domésticos", "domésticas"]
        },
        "it-IT": {
          "approved": "nucleo familiare",
          "banned": ["domestico", "domestica", "domestici", "domestiche"]
        }
      }
    },
    {
      "id": "household-label",
      "kind": "term",
      "rev": 2,
      "scope": "keys",
      "match": "Household",
      "note": "The household LABEL — the TelePortal hero banner and the setup dimension, whose English value is the bare word 'Household'. A one-word label IS the term, so unlike prose it is pinned to one exact string per locale; these are the two surfaces the production report named ('Seu Doméstico' is the guide prefixing teleportal.heroBanner.household). Longer labels ('Household Members', 'Household Type') stay under the prose rule above, where the banned list still rejects 'casa'/'lar' — the dwelling rather than the people. rev 2 (30 Sep): sentence case, as every UI label is ('Agregado familiar', 'Núcleo familiar').",
      "keys": ["teleportal.heroBanner.household", "teleportal.setupDimension.household"],
      "locales": {
        "pt-PT": { "approved": "Agregado familiar", "banned": [], "exact": true },
        "pt-BR": { "approved": "Núcleo familiar", "banned": [], "exact": true },
        "es-ES": { "approved": "Hogar", "banned": [], "exact": true },
        "it-IT": { "approved": "Nucleo familiare", "banned": [], "exact": true }
      }
    },
    {
      "id": "billing",
      "kind": "term",
      "rev": 1,
      "match": "billing",
      "note": "D-23. One term PER LOCALE, not one for both: pt-PT's navigation says 'Faturação' and pt-BR's says 'Cobrança'. 'Faturamento' is Brazilian and was leaking into pt-PT; 'Faturação' is European and does not belong in pt-BR. Note that pt-PT 'cobrança' stays legal where the ENGLISH says 'charge' — the scan is conditioned on the English source, so 'First Charge' -> 'Primeira Cobrança' is untouched.",
      "locales": {
        "pt-PT": { "approved": "faturação", "banned": ["faturamento", "cobrança"] },
        "pt-BR": { "approved": "cobrança", "banned": ["faturamento", "faturação"] },
        "es-ES": { "approved": "facturación", "banned": [] },
        "it-IT": { "approved": "fatturazione", "banned": [] }
      }
    },
    {
      "id": "relocation-kit",
      "kind": "term",
      "rev": 1,
      "match": "relocation kit",
      "note": "13 English strings rendered four different ways in it-IT and two in pt-BR. The approved rendering is the existing majority in each locale, so this is a consistency fix, not a rewording.",
      "locales": {
        "pt-PT": { "approved": "kit de realocação", "banned": ["kit de relocação"] },
        "pt-BR": { "approved": "kit de realocação", "banned": ["kit de relocação"] },
        "es-ES": { "approved": "kit de reubicación", "banned": ["kit de relocación"] },
        "it-IT": {
          "approved": "kit di trasferimento",
          "banned": ["kit di relocazione", "kit di rilocazione", "kit di riassegnazione"]
        }
      }
    },
    {
      "id": "journey",
      "kind": "term",
      "rev": 1,
      "match": "journey",
      "note": "F34. The product's word for a relocation is 'Jornada' (the switcher, the builder, the manage page), and pt-PT rendered the same English word as 'Viagem' in 77 strings — so one screen said 'Selecionar Viagem' above cards saying 'jornada'. 'Viagem' is correct where the English says TRAVEL (the TelePortal travel track, 'travel dates'), and the scan is conditioned on the English source, so those stay legal. pt-PT only: pt-BR is out of scope for this pass.",
      "locales": {
        "pt-PT": { "approved": "jornada", "banned": ["viagem", "viagens"] }
      }
    },
    {
      "id": "journeys",
      "kind": "term",
      "rev": 1,
      "match": "journeys",
      "note": "The plural of `journey`. Its own entry because matching is whole-word, so 'journey' does not match 'journeys' — by design (see the README).",
      "locales": {
        "pt-PT": { "approved": "jornadas", "banned": ["viagem", "viagens"] }
      }
    },
    {
      "id": "pt-pt-register-you",
      "kind": "term",
      "rev": 1,
      "translationOnly": true,
      "match": "you",
      "note": "F34. pt-PT addresses the reader in the implicit formal third person ('Pode…', 'o seu…', 'para si') — the register the majority of the file already uses. 194 strings used the Brazilian 'você', often in a sentence that was Brazilian throughout. Conditioned on the English 'you', which every one of them carried. pt-PT only.",
      "locales": {
        "pt-PT": {
          "approved": "tratamento formal implícito (Pode…, o seu…, si)",
          "banned": ["você", "vocês"]
        }
      }
    },
    {
      "id": "pt-pt-team",
      "kind": "term",
      "rev": 1,
      "translationOnly": true,
      "match": "team",
      "note": "F34. 'Equipe' is Brazilian; European Portuguese says 'equipa'. pt-PT only.",
      "locales": {
        "pt-PT": { "approved": "equipa", "banned": ["equipe", "equipes"] }
      }
    },
    {
      "id": "pt-pt-access",
      "kind": "term",
      "rev": 1,
      "translationOnly": true,
      "match": "access",
      "note": "F34. The VERB 'acessar' is Brazilian; European Portuguese says 'aceder'. The NOUN 'acesso' is correct in both and is deliberately not banned. pt-PT only.",
      "locales": {
        "pt-PT": {
          "approved": "aceder",
          "banned": ["acessar", "acesse", "acessem", "acessando", "acessado", "acessada"]
        }
      }
    },
    {
      "id": "nav-home",
      "kind": "term",
      "rev": 1,
      "scope": "keys",
      "keys": ["nav.home", "tabs.home", "business.nav.home", "business.layout.home"],
      "match": "Home",
      "note": "D-23, and it SUPERSEDES the DO_NOT_TRANSLATE_KEYS rationale that froze nav.home and business.layout.home in English. See the rationale, and the verdict on it, in packages/locale/scripts/i18n-shared.mjs. Key-scoped on purpose: 'home' must keep translating normally everywhere else ('home country' is not 'país de início').",
      "locales": {
        "pt-PT": { "approved": "Início", "banned": ["Casa", "Lar", "Home"], "exact": true },
        "pt-BR": { "approved": "Início", "banned": ["Casa", "Lar", "Home"], "exact": true },
        "es-ES": { "approved": "Inicio", "banned": ["Casa", "Hogar", "Home"], "exact": true },
        "it-IT": { "approved": "Home", "banned": ["Casa"], "exact": true }
      }
    },
    {
      "id": "pt-pt-type",
      "kind": "term",
      "rev": 1,
      "translationOnly": true,
      "match": "type",
      "note": "'Digite' (the verb 'digitar') is Brazilian; European Portuguese says 'Escreva'. 26 pt-PT strings said 'Digite' (30 Sep). pt-PT only.",
      "locales": {
        "pt-PT": { "approved": "escreva", "banned": ["digite", "digitar", "digitando", "digitem"] }
      }
    },
    {
      "id": "pt-pt-typing",
      "kind": "term",
      "rev": 1,
      "translationOnly": true,
      "match": "typing",
      "note": "The '-ing' form of `type` (matching is whole-word). 'Comece a digitar' is Brazilian; pt-PT says 'Comece a escrever'. pt-PT only.",
      "locales": {
        "pt-PT": { "approved": "escrever", "banned": ["digite", "digitar", "digitando"] }
      }
    },
    {
      "id": "pt-pt-enter",
      "kind": "term",
      "rev": 1,
      "translationOnly": true,
      "match": "enter",
      "note": "'Enter your …' in a form: pt-PT says 'Introduza …', never the Brazilian 'Digite …'. pt-PT only.",
      "locales": {
        "pt-PT": { "approved": "introduza", "banned": ["digite", "digitar"] }
      }
    },
    {
      "id": "pt-pt-senior",
      "kind": "term",
      "rev": 1,
      "translationOnly": true,
      "match": "senior",
      "note": "'Sênior' is the Brazilian spelling; European Portuguese writes 'Sénior' (the advanced-search job placeholder, 30 Sep). pt-PT only.",
      "locales": {
        "pt-PT": { "approved": "sénior", "banned": ["sênior"] }
      }
    }
  ]
}
